import { useTranslation } from "@nova/use-translation"
import { CreateFolder, Plus } from "@nova/component-icons"
import { ID } from "@nova/constants"
import { WorkspaceTreeMaxDepth } from "features/data-management/workspace/constants"
import { ButtonPrimary, ButtonTertiary } from "@nova/component-btn"
import {
    WorkspaceTreeItem,
    selectIsCreatingProject,
} from "features/data-management/workspace/WorkspaceManagementSlice"
import { useAppSelector } from "integrations/redux/hooks"
import { useMemo } from "react"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"

type WorkspaceBodyEmptySectionPros = {
    handleCreateFolder: (insideOfId?: ID) => Promise<void>
    handleCreateProject: () => Promise<void>
    activeFolder?: WorkspaceTreeItem
    createTagChain: (id: ID) => string[]
}

export const WorkspaceBodyEmptySection = ({
    handleCreateFolder,
    handleCreateProject,
    activeFolder,
    createTagChain,
}: WorkspaceBodyEmptySectionPros) => {
    const { t } = useTranslation()
    const { isEditor } = useWorkspacePermissions(activeFolder?.id)
    const tagIds = useMemo(
        () => (activeFolder ? createTagChain(activeFolder.id) : []),
        [activeFolder, createTagChain]
    )
    const canCreateFolder = isEditor && tagIds.length <= WorkspaceTreeMaxDepth
    const isCreatingProject = useAppSelector(selectIsCreatingProject())

    return (
        <div className="w-full h-full flex flex-col justify-center items-center gap-8 select-none py-20">
            <div className="flex flex-col items-center gap-2">
                <small className="xs text-white-400">{t("Nothing here yet")}</small>
                <h1 className="text-gray-0">
                    {t(tagIds.length === 0 ? "Create your first project." : "Create a project.")}
                </h1>
                {tagIds.length === 0 && (
                    <h5 className="text-gray-100 text-center">
                        <span>{t("Start using Nova by creating your first project.")}</span>
                        <br />
                        <span>{t("Need more help to get started? Check out our ")}</span>
                        {/* Copies H5 styling */}
                        <a
                            className="text-gray-100 text-sm font-normal leading-[140%]"
                            target="_blank"
                            rel="noopener noreferrer"
                            href="https://learn.mapnova.com"
                        >
                            {t("tutorials")}
                        </a>
                        <span>{t(".")}</span>
                    </h5>
                )}
            </div>
            <div className="flex gap-2">
                {canCreateFolder && (
                    <ButtonTertiary
                        LeftIcon={CreateFolder}
                        title="New Folder"
                        onClick={() => handleCreateFolder(tagIds.at(-1))}
                    />
                )}
                {isEditor && (
                    <ButtonPrimary
                        LeftIcon={Plus}
                        title="New Project"
                        onClick={handleCreateProject}
                        isLoading={isCreatingProject}
                    />
                )}
            </div>
        </div>
    )
}
